import Phaser from 'phaser';
import { PowerUp, POWER_UP_CONFIGS } from '../sprites/PowerUp';
import type { PowerUpType } from '../sprites/PowerUp';
import { ToasterTruck } from '../sprites/ToasterTruck';
import { getDifficultySettings } from '../config/difficultySettings';

export interface ActivePowerUp {
  type: PowerUpType;
  endTime: number;
  shieldHits?: number; // For butter shield
}

export class PowerUpManager {
  private scene: Phaser.Scene;
  private powerUpGroup: Phaser.Physics.Arcade.Group;
  private truck: ToasterTruck;
  private activePowerUps: Map<PowerUpType, ActivePowerUp> = new Map();
  private onPowerUpChange?: (actives: ActivePowerUp[]) => void;

  constructor(scene: Phaser.Scene, truck: ToasterTruck) {
    this.scene = scene;
    this.truck = truck;

    this.powerUpGroup = scene.physics.add.group({
      classType: PowerUp,
      runChildUpdate: true,
    });
  }

  getGroup(): Phaser.Physics.Arcade.Group {
    return this.powerUpGroup;
  }

  trySpawnPowerUp(x: number, y: number): void {
    const settings = getDifficultySettings();
    
    if (Math.random() < settings.powerUpDropRate) {
      // Random power-up type
      const types: PowerUpType[] = ['rapidFire', 'butterShield', 'jamSpread'];
      const type = types[Math.floor(Math.random() * types.length)];
      
      new PowerUp(this.scene, x, y, type, this.powerUpGroup);
    }
  }

  collectPowerUp(powerUp: PowerUp): void {
    const type = powerUp.powerUpType;
    const config = POWER_UP_CONFIGS[type];
    const currentTime = this.scene.time.now;

    // Create or extend the power-up
    const existing = this.activePowerUps.get(type);
    
    if (existing) {
      // Extend duration
      existing.endTime = currentTime + config.duration;
      if (type === 'butterShield') {
        existing.shieldHits = 3; // Reset shield hits
      }
    } else {
      // New power-up
      const active: ActivePowerUp = {
        type,
        endTime: currentTime + config.duration,
      };
      
      if (type === 'butterShield') {
        active.shieldHits = 3;
      }
      
      this.activePowerUps.set(type, active);
    }

    // Apply effect to truck
    this.applyPowerUpEffect(type);
    
    // Notify UI
    this.notifyChange();

    // Visual feedback
    this.showCollectEffect(powerUp.x, powerUp.y, config.color);
    
    // Destroy the power-up sprite
    powerUp.destroy();
  }

  private applyPowerUpEffect(type: PowerUpType): void {
    switch (type) {
      case 'rapidFire':
        this.truck.setRapidFire(true);
        break;
      case 'butterShield':
        this.truck.setShieldActive(true);
        break;
      case 'jamSpread':
        this.truck.setSpreadShot(true);
        break;
    }
  }

  private removePowerUpEffect(type: PowerUpType): void {
    switch (type) {
      case 'rapidFire':
        this.truck.setRapidFire(false);
        break;
      case 'butterShield':
        this.truck.setShieldActive(false);
        break;
      case 'jamSpread':
        this.truck.setSpreadShot(false);
        break;
    }
  }

  update(time: number): void {
    // Check for expired power-ups
    const expired: PowerUpType[] = [];
    
    this.activePowerUps.forEach((active, type) => {
      if (time > active.endTime) {
        expired.push(type);
      }
    });

    // Remove expired power-ups
    expired.forEach((type) => {
      this.activePowerUps.delete(type);
      this.removePowerUpEffect(type);
    });

    if (expired.length > 0) {
      this.notifyChange();
    }
  }

  // Called when player takes damage - checks shield
  onPlayerHit(): boolean {
    const shield = this.activePowerUps.get('butterShield');
    
    if (shield && shield.shieldHits && shield.shieldHits > 0) {
      shield.shieldHits--;
      
      // Show shield hit effect
      this.showShieldHitEffect();
      
      if (shield.shieldHits <= 0) {
        this.activePowerUps.delete('butterShield');
        this.removePowerUpEffect('butterShield');
      }
      
      this.notifyChange();
      return true; // Damage was absorbed
    }
    
    return false; // No shield, take damage
  }

  private showCollectEffect(x: number, y: number, color: number): void {
    // Expanding ring
    const ring = this.scene.add.circle(x, y, 10, color, 0.8);
    
    this.scene.tweens.add({
      targets: ring,
      radius: 40,
      alpha: 0,
      duration: 300,
      onComplete: () => ring.destroy(),
    });

    // Text popup
    const text = this.scene.add.text(x, y - 20, 'POWER UP!', {
      fontFamily: 'Arial Black',
      fontSize: '16px',
      color: '#ffffff',
    }).setOrigin(0.5);

    this.scene.tweens.add({
      targets: text,
      y: y - 60,
      alpha: 0,
      duration: 800,
      onComplete: () => text.destroy(),
    });
  }

  private showShieldHitEffect(): void {
    // Flash the truck with shield color
    this.truck.setTint(0xffdd00);
    this.scene.time.delayedCall(100, () => {
      this.truck.clearTint();
    });

    // Show remaining hits
    const shield = this.activePowerUps.get('butterShield');
    if (shield?.shieldHits !== undefined) {
      const text = this.scene.add.text(this.truck.x, this.truck.y - 40, 
        `SHIELD: ${shield.shieldHits}`, {
        fontFamily: 'Arial Black',
        fontSize: '14px',
        color: '#ffdd00',
      }).setOrigin(0.5);

      this.scene.tweens.add({
        targets: text,
        y: this.truck.y - 70,
        alpha: 0,
        duration: 600,
        onComplete: () => text.destroy(),
      });
    }
  }

  setOnPowerUpChange(callback: (actives: ActivePowerUp[]) => void): void {
    this.onPowerUpChange = callback;
  }

  private notifyChange(): void {
    const actives = Array.from(this.activePowerUps.values());
    this.onPowerUpChange?.(actives);
  }

  getActivePowerUps(): ActivePowerUp[] {
    return Array.from(this.activePowerUps.values());
  }

  hasShield(): boolean {
    const shield = this.activePowerUps.get('butterShield');
    return !!(shield && shield.shieldHits && shield.shieldHits > 0);
  }

  reset(): void {
    this.activePowerUps.clear();
    this.truck.setRapidFire(false);
    this.truck.setShieldActive(false);
    this.truck.setSpreadShot(false);
  }
}

